import axios from "axios"
import { useStoreActions, useStoreState } from "easy-peasy"
import { useEffect, useState } from "react"
import React from "react"
import { useNavigate } from "react-router-dom"
import { beep } from "./beep.js"
import ErrorPopup from "./ErrorPopup.js"
import StatusPopup from "./StatusPopup.js"
import { DASHBOARD_DEPUTY_RANKING_URL, DASHBOARD_FARMS_URL, DASHBOARD_RECEIPTS_URL, DASHBOARD_STATS_URL, DOG_TAGS_STAFF_URL, DOG_TAGS_URL } from "./Constants.js"

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

const fmt = (val) => (val === 0 ? "" : `$${val.toFixed(2)}`)
const fmtTotal = (val) => `$${val.toFixed(2)}`

const Dashboard = () => {
	const d = new Date()
	// Tag inventory is a single fiscal year now (Jul 1 - Jun 30, labeled by start year)
	const fiscalYear = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1
	const navigate = useNavigate()
	const Token = useStoreState((state) => state.Token)
	const setLoggedIn = useStoreActions((actions) => actions.setLoggedIn)
	const setHeaderCity = useStoreActions((actions) => actions.setHeaderCity)
	const setHeaderUser = useStoreActions((actions) => actions.setHeaderUser)

	// Receipts state
	const [rows, setRows] = useState([])
	const [receiptStatus, setReceiptStatus] = useState("loading")

	// Statistics state
	const [statsYear, setStatsYear] = useState(null)
	const [statsRows, setStatsRows] = useState([])
	const [statsTotals, setStatsTotals] = useState(null)
	const [statsStatus, setStatsStatus] = useState("loading")

	// Deputy Ranking state
	const [rankingYear, setRankingYear] = useState(null)
	const [rankingRows, setRankingRows] = useState([])
	const [rankingTotals, setRankingTotals] = useState(null)
	const [rankingStatus, setRankingStatus] = useState("loading")

	// Farms summary (by year / status)
	const [farmRows, setFarmRows] = useState([])
	const [farmStatus, setFarmStatus] = useState("loading")

	// Tag Inventory state
	const [tagYear] = useState(fiscalYear)
	const [filterStatus, setFilterStatus] = useState("")
	const [filterStaff, setFilterStaff] = useState("")
	const [staffList, setStaffList] = useState([])
	const [inventory, setInventory] = useState([])
	const [invStatus, setInvStatus] = useState("")
	const [searched, setSearched] = useState(false)
	const [invError, setInvError] = useState("")

	useEffect(() => { if (invError) beep() }, [invError])
	useEffect(() => {
		if (!(inventory.length === 0 && searched && invStatus === "")) return
		const t = setTimeout(() => setSearched(false), 2000)
		return () => clearTimeout(t)
	}, [inventory, searched, invStatus])

	useEffect(() => {
		const payload = { login_id: Token.login_id, client_id: Token.client_id }

		const fetchReceipts = async () => {
			try {
				const { data } = await axios.post(DASHBOARD_RECEIPTS_URL, payload)
				setRows(data.rows)
				setReceiptStatus(data.rows.length === 0 ? "empty" : "ok")
			} catch {
				setReceiptStatus("error")
			}
		}

		const fetchStats = async () => {
			try {
				const { data } = await axios.post(DASHBOARD_STATS_URL, payload)
				setStatsYear(data.year)
				setStatsRows(data.rows)
				setStatsTotals(data.totals)
				setStatsStatus(data.rows.length === 0 ? "empty" : "ok")
			} catch {
				setStatsStatus("error")
			}
		}

		const loadStaff = async () => {
			try {
				const { data } = await axios.post(DOG_TAGS_STAFF_URL, payload)
				setStaffList(data)
			} catch (err) {
				setInvError(err.message)
			}
		}

		const fetchFarms = async () => {
			try {
				const { data } = await axios.post(DASHBOARD_FARMS_URL, payload)
				// Pivot the per-(year,status) rows into one row per year with a count per status.
				const byYear = new Map()
				data.rows.forEach((r) => {
					if (!byYear.has(r.year)) byYear.set(r.year, { year: r.year, Pending: 0, Granted: 0, Denied: 0, Withdrawn: 0 })
					const b = byYear.get(r.year)
					if (r.status in b) b[r.status] += r.count
				})
				setFarmRows([...byYear.values()])
				setFarmStatus(data.rows.length === 0 ? "empty" : "ok")
			} catch {
				setFarmStatus("error")
			}
		}

		const fetchDeputyRanking = async () => {
			try {
				const { data } = await axios.post(DASHBOARD_DEPUTY_RANKING_URL, payload)
				setRankingYear(data.year)
				setRankingRows(data.rows)
				setRankingTotals(data.totals)
				setRankingStatus(data.rows.length === 0 ? "empty" : "ok")
			} catch {
				setRankingStatus("error")
			}
		}

		fetchReceipts()
		fetchStats()
		fetchDeputyRanking()
		fetchFarms()
		loadStaff()
	}, [Token.login_id, Token.client_id])

	useEffect(() => {
		if (!invError) return
		const timer = setTimeout(() => {
			if (invError.includes("401")) {
				setHeaderCity("CR Cloud Technology")
				setHeaderUser("None")
				setLoggedIn(false)
				navigate("/login")
			}
		}, 2000)
		return () => clearTimeout(timer)
	}, [invError, navigate, setHeaderCity, setHeaderUser, setLoggedIn])

	async function handleSearch(e) {
		e.preventDefault()
		setInvStatus("submitting")
		setInventory([])
		try {
			const { data } = await axios.post(DOG_TAGS_URL, {
				login_id: Token.login_id,
				client_id: Token.client_id,
				tag_year: tagYear,
				status: filterStatus,
				assigned_to: filterStaff ? Number(filterStaff) : null,
			})
			setInventory(data)
			setSearched(true)
			setInvStatus("")
		} catch (err) {
			setInvStatus("")
			setSearched(true)
			if (err.message === "Request failed with status code 401") {
				setInvError("Timeout")
			} else if (err.message === "Request failed with status code 422") {
				setInvError("422")
			} else if (err.message === "Request failed with status code 500") {
				setInvError("500")
			} else {
				setInvError(err.message)
			}
		}
	}

	return (
		<main className="Main">
			<fieldset style={{ width: "fit-content", margin: "0 auto" }}>
				<legend>Receipts</legend>
				{receiptStatus === "loading" && <StatusPopup message="Loading..." />}
				{receiptStatus === "error" && <StatusPopup message="Failed to load receipts." />}
				{receiptStatus === "empty" && <StatusPopup message="No receipt data found." />}
				{receiptStatus === "ok" && (
					<div style={{ overflowX: "auto", display: "inline-block" }}>
						<table className="receipts-table" style={{ width: "fit-content" }}>
							<thead>
								<tr>
									<th style={{ textAlign: "left" }}>Deputy</th>
									<th style={{ textAlign: "center" }}>Year</th>
									{MONTHS.map((m) => (
										<th key={m} style={{ textAlign: "right" }}>{m}</th>
									))}
									<th style={{ textAlign: "right" }}>Total</th>
								</tr>
							</thead>
							<tbody>
								{(() => {
									const years = [...new Set(rows.map((r) => r.year))].sort()
									return years.map((yr) => {
										const yearRows = rows.filter((r) => r.year === yr)
										const yrMonths = MONTHS.map((_, mi) =>
											yearRows.reduce((s, r) => s + r.months[mi], 0)
										)
										const yrTotal = yearRows.reduce((s, r) => s + r.row_total, 0)
										return (
											<React.Fragment key={yr}>
												{yearRows.map((row, idx) => (
													<tr key={idx}>
														<td style={{ whiteSpace: "nowrap" }}>{row.deputy_name}</td>
														<td style={{ textAlign: "center" }}>{row.year}</td>
														{row.months.map((val, mi) => (
															<td key={mi} style={{ textAlign: "right" }}>{fmt(val)}</td>
														))}
														<td style={{ textAlign: "right", fontWeight: "bold" }}>{fmtTotal(row.row_total)}</td>
													</tr>
												))}
												<tr className="receipts-year-total">
													<td colSpan={2}>{yr} Total</td>
													{yrMonths.map((val, mi) => (
														<td key={mi} style={{ textAlign: "right" }}>{fmtTotal(val)}</td>
													))}
													<td style={{ textAlign: "right" }}>{fmtTotal(yrTotal)}</td>
												</tr>
											</React.Fragment>
										)
									})
								})()}
							</tbody>
						</table>
					</div>
				)}
			</fieldset>

			<div style={{ display: "flex", gap: "1rem", justifyContent: "center", alignItems: "flex-start", flexWrap: "wrap" }}>
				<fieldset style={{ width: "fit-content" }}>
					<legend>Statistics {statsYear ?? ""}</legend>
					{statsStatus === "loading" && <StatusPopup message="Loading..." />}
					{statsStatus === "error" && <StatusPopup message="Failed to load statistics." />}
					{statsStatus === "empty" && <StatusPopup message={`No data found for ${statsYear}.`} />}
					{statsStatus === "ok" && (
						<div style={{ overflowX: "auto" }}>
							<table className="receipts-table">
								<thead>
									<tr>
										<th style={{ textAlign: "left" }}>District</th>
										<th style={{ textAlign: "right" }}>Taxpayers</th>
										<th style={{ textAlign: "right" }}>Dogs</th>
										<th style={{ textAlign: "right" }}>Sheep</th>
										<th style={{ textAlign: "right" }}>Goats</th>
										<th style={{ textAlign: "right" }}>Taxes</th>
									</tr>
								</thead>
								<tbody>
									{statsRows.map((row, idx) => (
										<tr key={idx}>
											<td>{row.district}</td>
											<td style={{ textAlign: "right" }}>{row.taxpayers || ""}</td>
											<td style={{ textAlign: "right" }}>{row.dogs || ""}</td>
											<td style={{ textAlign: "right" }}>{row.sheep || ""}</td>
											<td style={{ textAlign: "right" }}>{row.goats || ""}</td>
											<td style={{ textAlign: "right" }}>{row.taxes ? fmtTotal(row.taxes) : ""}</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr>
										<td style={{ fontWeight: "bold" }}>Total</td>
										<td style={{ textAlign: "right", fontWeight: "bold" }}>{statsTotals.taxpayers}</td>
										<td style={{ textAlign: "right", fontWeight: "bold" }}>{statsTotals.dogs}</td>
										<td style={{ textAlign: "right", fontWeight: "bold" }}>{statsTotals.sheep}</td>
										<td style={{ textAlign: "right", fontWeight: "bold" }}>{statsTotals.goats}</td>
										<td style={{ textAlign: "right", fontWeight: "bold" }}>{statsTotals.taxes != null ? fmtTotal(statsTotals.taxes) : ""}</td>
									</tr>
								</tfoot>
							</table>
						</div>
					)}
				</fieldset>

				<fieldset style={{ width: "fit-content" }}>
					<legend>Deputy Ranking {rankingYear}</legend>
					{rankingStatus === "loading" && <StatusPopup message="Loading..." />}
					{rankingStatus === "error" && <StatusPopup message="Failed to load rankings." />}
					{rankingStatus === "empty" && <StatusPopup message={`No transaction data for ${rankingYear}.`} />}
					{rankingStatus === "ok" && (
						<table className="receipts-table">
							<thead>
								<tr>
									<th style={{ textAlign: "left" }}>Deputy</th>
									<th style={{ textAlign: "right" }}>Taxpayers</th>
									<th style={{ textAlign: "right" }}>Email</th>
									<th style={{ textAlign: "right" }}>Phone</th>
									<th style={{ textAlign: "right" }}>Dogs</th>
									<th style={{ textAlign: "right" }}>Sheep</th>
									<th style={{ textAlign: "right" }}>Goats</th>
									<th style={{ textAlign: "right" }}>Taxes</th>
								</tr>
							</thead>
							<tbody>
								{rankingRows.map((row, idx) => (
									<tr key={idx}>
										<td style={{ whiteSpace: "nowrap" }}>{row.deputy}</td>
										<td style={{ textAlign: "right" }}>{row.taxpayers || ""}</td>
										<td style={{ textAlign: "right" }}>{row.emails || ""}</td>
										<td style={{ textAlign: "right" }}>{row.phones || ""}</td>
										<td style={{ textAlign: "right" }}>{row.dogs || ""}</td>
										<td style={{ textAlign: "right" }}>{row.sheep || ""}</td>
										<td style={{ textAlign: "right" }}>{row.goats || ""}</td>
										<td style={{ textAlign: "right" }}>{fmtTotal(row.taxes)}</td>
									</tr>
								))}
							</tbody>
							{rankingTotals && (
							<tfoot>
								<tr>
									<td style={{ fontWeight: "bold" }}>Total</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.taxpayers}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.emails}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.phones}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.dogs}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.sheep}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{rankingTotals.goats}</td>
									<td style={{ textAlign: "right", fontWeight: "bold" }}>{fmtTotal(rankingTotals.taxes)}</td>
								</tr>
							</tfoot>
						)}
						</table>
					)}
				</fieldset>
			</div>

			<fieldset style={{ width: "fit-content", margin: "0 auto" }}>
				<legend>Farms</legend>
				{farmStatus === "loading" && <StatusPopup message="Loading..." />}
				{farmStatus === "error" && <StatusPopup message="Failed to load farms." />}
				{farmStatus === "empty" && <StatusPopup message="No farms found." />}
				{farmStatus === "ok" && (
					<div style={{ overflowX: "auto" }}>
						<table className="receipts-table">
							<thead>
								<tr>
									<th style={{ textAlign: "left" }}>Year</th>
									<th style={{ textAlign: "right" }}>Pending</th>
									<th style={{ textAlign: "right" }}>Granted</th>
									<th style={{ textAlign: "right" }}>Denied</th>
									<th style={{ textAlign: "right" }}>Withdrawn</th>
									<th style={{ textAlign: "right" }}>Total</th>
								</tr>
							</thead>
							<tbody>
								{farmRows.map((row) => (
									<tr key={row.year}>
										<td>{`${row.year - 1} - ${row.year}`}</td>
										<td style={{ textAlign: "right" }}>{row.Pending}</td>
										<td style={{ textAlign: "right" }}>{row.Granted}</td>
										<td style={{ textAlign: "right" }}>{row.Denied}</td>
										<td style={{ textAlign: "right" }}>{row.Withdrawn}</td>
										<td style={{ textAlign: "right" }}>{row.Pending + row.Granted + row.Denied + row.Withdrawn}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</fieldset>

			<div className="dogtags-scope">
				<fieldset className="tag_inventory">
					<legend>Tag Inventory</legend>
					<form onSubmit={handleSearch}>
						<span style={{ display: "inline-block", marginRight: "15px" }}>
							<label style={{ display: "block" }}>Fiscal Year:</label>
							<div style={{ marginTop: "0.25rem" }}>July 1, {fiscalYear} - June 30, {fiscalYear + 1}</div>
						</span>
						<span style={{ display: "inline-block", marginRight: "15px" }}>
							<label htmlFor="inv_status" style={{ display: "block" }}>Status:</label>
							<select
								id="inv_status"
								name="inv_status"
								value={filterStatus}
								onChange={(e) => setFilterStatus(e.target.value)}
							>
								<option value="">All</option>
								<option value="All Tagged Dogs">All Tagged Dogs</option>
								<option value="Unassigned">Unassigned</option>
								<option value="Assigned">Assigned</option>
								<option value="Active">Active</option>
								<option value="Replaced">Replaced</option>
								<option value="Service Dog">Service Dog</option>
								<option value="Kennel">Kennel</option>
							</select>
						</span>
						<span style={{ display: "inline-block", marginRight: "15px" }}>
							<label htmlFor="inv_staff" style={{ display: "block" }}>Staff:</label>
							<select
								id="inv_staff"
								name="inv_staff"
								value={filterStaff}
								onChange={(e) => setFilterStaff(e.target.value)}
							>
								<option value="">All</option>
								{staffList.map((s) => (
									<option key={s[0]} value={s[0]}>{s[2]}, {s[3]}</option>
								))}
							</select>
						</span>
						<div style={{ textAlign: "center", marginBottom: "1rem" }}>
							<button className="saveBtn" type="submit" disabled={invStatus === "submitting"}>
								Search
							</button>
						</div>
						<ErrorPopup
							message={
								invError === "Timeout" ? "Your session has timed out. Please login again." :
								invError === "422" ? "Unable to use browser refresh button." :
								invError === "500" ? "Unknown error — notify CR Cloud Technology." :
								invError
							}
							onClose={() => setInvError("")}
						/>
					</form>
					{inventory.length > 0 && (
						<div className="table_container">
							<table>
								<thead>
									<tr>
										<th>Tag #</th>
										<th>Dog Name</th>
										<th>Status</th>
										<th>Assigned To</th>
										<th>Issued</th>
										<th>Issued By</th>
										<th>Taxpayer</th>
									</tr>
								</thead>
								<tbody>
									{inventory.map((tag) => (
										<tr key={tag[0]}>
											<td>{tag[3]}</td>
											<td>{tag[4]}</td>
											<td>{tag[5]}</td>
											<td>{tag[6]}</td>
											<td>{tag[7]}</td>
											<td>{tag[8]}</td>
											<td>{tag[9]}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{inventory.length === 0 && searched && invStatus === "" && (
						<StatusPopup message="No tags found." />
					)}
				</fieldset>
			</div>
		</main>
	)
}

export default Dashboard
